import { TechlogEntry, PilotStats, AircraftStats, Sector } from '../types';

// Flight time limits
const FLIGHT_TIME_LIMITS = {
  MAX_SECTORS_24H: 10,
  MAX_HOURS_7D: 34,
  MAX_HOURS_28D: 100,
  MAX_HOURS_365D: 1000,
};

// Duty time limits
const DUTY_TIME_LIMITS = {
  MAX_HOURS_7D: 55,
  MAX_HOURS_28D: 190,
  MAX_HOURS_365D: 1800,
};

export const calculateFlightTime = (sectors: Sector[]): number => {
  const total = sectors.reduce((total, sector) => total + sector.flightTime, 0);
  return Math.floor(total * 10) / 10;
};

export const calculateDutyTime = (sectors: Sector[]): number => {
  if (sectors.length === 0) return 0;
  
  // Sort sectors by takeoff time
  const sortedSectors = [...sectors].sort((a, b) => {
    const timeA = new Date(`2000-01-01T${a.takeoffTime}`).getTime();
    const timeB = new Date(`2000-01-01T${b.takeoffTime}`).getTime();
    return timeA - timeB;
  });
  
  const takeoffTimes = sortedSectors.map(s => new Date(`2000-01-01T${s.takeoffTime}`).getTime());
  const landingTimes = sortedSectors.map(s => new Date(`2000-01-01T${s.landingTime}`).getTime());
  
  const earliestTakeoff = Math.min(...takeoffTimes);
  const latestLanding = Math.max(...landingTimes);
  
  // Subtract 45 minutes from earliest takeoff (duty start)
  const dutyStart = earliestTakeoff - (45 * 60 * 1000);
  // Add 15 minutes to latest landing (duty end)
  const dutyEnd = latestLanding + (15 * 60 * 1000);
  
  const baseDutyTimeMs = dutyEnd - dutyStart;
  let dutyTimeHours = baseDutyTimeMs / (1000 * 60 * 60);
  
  // Calculate split duty periods (gaps of 3+ hours between sectors)
  let splitDutyTimeMs = 0;
  for (let i = 0; i < sortedSectors.length - 1; i++) {
    const currentSector = sortedSectors[i];
    const nextSector = sortedSectors[i + 1];

    const landingTime = new Date(`2000-01-01T${currentSector.landingTime}`).getTime();
    const nextTakeoffTime = new Date(`2000-01-01T${nextSector.takeoffTime}`).getTime();

    // Calculate gap between landing and next takeoff
    const gapMs = nextTakeoffTime - landingTime;
    const gapHours = gapMs / (1000 * 60 * 60);

    // If gap is 3 hours or more, it's split duty (rest period)
    if (gapHours >= 3) {
      splitDutyTimeMs += gapMs;
    }
  }

  // Subtract split duty time from total duty time
  const splitDutyTimeHours = splitDutyTimeMs / (1000 * 60 * 60);
  dutyTimeHours = dutyTimeHours - splitDutyTimeHours;
  
  return Math.max(0, dutyTimeHours); // Ensure non-negative
};

// New function to get split duty details for a day's sectors
export const getSplitDutyDetails = (sectors: Sector[]): Array<{
  afterSector: number;
  startTime: string;
  endTime: string;
  durationHours: number;
}> => {
  if (sectors.length <= 1) return [];

  const sortedSectors = [...sectors].sort((a, b) => {
    const timeA = new Date(`2000-01-01T${a.takeoffTime}`).getTime();
    const timeB = new Date(`2000-01-01T${b.takeoffTime}`).getTime();
    return timeA - timeB;
  });

  const splitDutyPeriods: Array<{
    afterSector: number;
    startTime: string;
    endTime: string;
    durationHours: number;
  }> = [];

  for (let i = 0; i < sortedSectors.length - 1; i++) {
    const currentSector = sortedSectors[i];
    const nextSector = sortedSectors[i + 1];

    const landingTime = new Date(`2000-01-01T${currentSector.landingTime}`).getTime();
    const nextTakeoffTime = new Date(`2000-01-01T${nextSector.takeoffTime}`).getTime();

    const gapMs = nextTakeoffTime - landingTime;
    const gapHours = gapMs / (1000 * 60 * 60);

    if (gapHours >= 3) {
      splitDutyPeriods.push({
        afterSector: i + 1,
        startTime: currentSector.landingTime,
        endTime: nextSector.takeoffTime,
        durationHours: gapHours
      });
    }
  }

  return splitDutyPeriods;
};

export const getEntriesInDateRange = (
  entries: TechlogEntry[],
  startDate: Date,
  endDate: Date
): TechlogEntry[] => {
  return entries.filter(entry => {
    const entryDate = new Date(entry.date);
    return entryDate >= startDate && entryDate <= endDate;
  });
};

export const calculatePilotStats = (
  pilotName: string,
  entries: TechlogEntry[]
): PilotStats => {
  const now = new Date();
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const twentyEightDaysAgo = new Date(now.getTime() - 28 * 24 * 60 * 60 * 1000);
  const threeSixtyFiveDaysAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);

  const pilotEntries = entries.filter(entry => 
    entry.pilotName === pilotName || entry.coPilotName === pilotName
  );

  const entries7Days = getEntriesInDateRange(pilotEntries, sevenDaysAgo, now);
  const entries28Days = getEntriesInDateRange(pilotEntries, twentyEightDaysAgo, now);
  const entries365Days = getEntriesInDateRange(pilotEntries, threeSixtyFiveDaysAgo, now);

  // Calculate flight times
  const flightTime7Days = entries7Days.reduce((total, entry) => 
    total + calculateFlightTime(entry.sectors), 0
  );
  const flightTime28Days = entries28Days.reduce((total, entry) => 
    total + calculateFlightTime(entry.sectors), 0
  );
  const flightTime365Days = entries365Days.reduce((total, entry) => 
    total + calculateFlightTime(entry.sectors), 0
  );

  // Calculate duty times
  const dutyTime7Days = entries7Days.reduce((total, entry) => 
    total + calculateDutyTime(entry.sectors), 0
  );
  const dutyTime28Days = entries28Days.reduce((total, entry) => 
    total + calculateDutyTime(entry.sectors), 0
  );
  const dutyTime365Days = entries365Days.reduce((total, entry) => 
    total + calculateDutyTime(entry.sectors), 0
  );

  // Calculate sectors
  const sectors7Days = entries7Days.reduce((total, entry) => 
    total + entry.sectors.length, 0
  );
  const sectors28Days = entries28Days.reduce((total, entry) => 
    total + entry.sectors.length, 0
  );
  const sectors365Days = entries365Days.reduce((total, entry) => 
    total + entry.sectors.length, 0
  );

  // Check for 24-hour sector limit (last 24 hours)
  const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const entries24Hours = getEntriesInDateRange(pilotEntries, twentyFourHoursAgo, now);
  const sectors24Hours = entries24Hours.reduce((total, entry) => 
    total + entry.sectors.length, 0
  );

  return {
    pilotName,
    flightTime7Days,
    flightTime28Days,
    flightTime365Days,
    dutyTime7Days,
    dutyTime28Days,
    dutyTime365Days,
    sectors7Days,
    sectors28Days,
    sectors365Days,
    exceedances: {
      flightTime7Days: flightTime7Days > FLIGHT_TIME_LIMITS.MAX_HOURS_7D,
      flightTime28Days: flightTime28Days > FLIGHT_TIME_LIMITS.MAX_HOURS_28D,
      flightTime365Days: flightTime365Days > FLIGHT_TIME_LIMITS.MAX_HOURS_365D,
      dutyTime7Days: dutyTime7Days > DUTY_TIME_LIMITS.MAX_HOURS_7D,
      dutyTime28Days: dutyTime28Days > DUTY_TIME_LIMITS.MAX_HOURS_28D,
      dutyTime365Days: dutyTime365Days > DUTY_TIME_LIMITS.MAX_HOURS_365D,
      sectors24Hours: sectors24Hours > FLIGHT_TIME_LIMITS.MAX_SECTORS_24H,
    },
  };
};

export const calculateAircraftStats = (
  registration: string,
  entries: TechlogEntry[]
): AircraftStats => {
  const aircraftEntries = entries.filter(entry => 
    entry.aircraftRegistration === registration
  );

  const totalCycles = aircraftEntries.reduce((total, entry) => 
    total + entry.sectors.length, 0
  );

  const totalFlightHours = aircraftEntries.reduce((total, entry) => 
    total + calculateFlightTime(entry.sectors), 0
  );

  return {
    registration,
    totalCycles,
    totalFlightHours,
    lastUpdated: new Date(),
  };
};

export const getAllPilotNames = (entries: TechlogEntry[]): string[] => {
  const pilotNames = new Set<string>();
  
  entries.forEach(entry => {
    pilotNames.add(entry.pilotName);
    if (entry.coPilotName) {
      pilotNames.add(entry.coPilotName);
    }
  });
  
  return Array.from(pilotNames).sort();
};

export const getAllAircraftRegistrations = (entries: TechlogEntry[]): string[] => {
  const registrations = new Set<string>();
  
  entries.forEach(entry => {
    registrations.add(entry.aircraftRegistration);
  });
  
  return Array.from(registrations).sort();
};
